/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Box,
  Typography,
  Divider,
  Grid,
  CircularProgress,
  Tabs,
  Tab,
  Stack,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import customAxios from '../../utils/customAxios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';

import '../../scss/Pages/_techDetails.scss';
import { GlobalVariables } from '../../globalVar';
import { useTableLocale } from '../../hooks/useTableLocale';
import { getToast } from '../../utils/toastConfig';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';

const LOCAL_COLUMN_WIDTH = GlobalVariables.getDefaultColumnWidth();

const HEADERS = {
  headers: {
    Authorization: 'bearer ' + localStorage.getItem('token'),
  },
};

const TechDetails = () => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [techDetails, setTechDetails] = useState({});
  const [Logs, setLogs] = useState([]);
  const [availableVisits, setAvailableVisits] = useState([]);
  const [assignedVisits, setAssignedVisits] = useState([]);
  const [selectedTab, setSelectedTab] = useState(1);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });
  const [assignedPaginationModel, setAssignedPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });
  const [assignedRowCount, setAssignedRowCount] = useState(0);

  const navigate = useNavigate();

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const responseTechDetail = await customAxios.get(
        `${GlobalVariables.getURL()}techDetails/${id}`,
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        },
      );

      if (
        !responseTechDetail.data.tech_location &&
        !responseTechDetail.data.tech_logs &&
        JSON.stringify(responseTechDetail.data.visit_info === '{}')
      ) {
        getToast({
          status: 'error',
          title: 'Error al intentar ver los detalles del técnico.',
        });
        toast.onChange((payload) => {
          if (payload.status === 'removed') navigate('/dashboard');
        });
      }

      const responseAvailableVisits = await customAxios.post(
        `${GlobalVariables.getURL()}unAssignedVisits`,
        {
          pagination: {
            rowsPerPage: paginationModel.pageSize,
            page: paginationModel.page + 1,
          },
        },
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        },
      );

      setAvailableVisits(responseAvailableVisits.data?.rows ?? []);
      setRowCount(responseAvailableVisits.data?.totalRows ?? 0);

      const groupLogsByDate = (responseTechDetail.data.tech_logs ?? []).reduce(
        (acc, log) => {
          const date = new Date(log.updated_at).toLocaleDateString();
          const time = new Date(log.updated_at).toLocaleTimeString(undefined, {
            hour: '2-digit',
            minute: '2-digit',
          });

          if (!acc[date]) acc[date] = [];
          acc[date].push({ time, reason: log.updated_reason });
          return acc;
        },
        {},
      );

      setTechDetails(responseTechDetail.data);
      setLogs(groupLogsByDate);
      setIsLoading(false);
    } catch (err) {
      console.log('🚀 ~ err:', err);
    }
  };

  const fetchAssignedVisits = async () => {
    const endpoint = `${GlobalVariables.getURL()}getAssignedVisitsForTechDetails/${id}`;
    setIsLoading(true);

    try {
      const response = await customAxios.post(
        endpoint,
        {
          pagination: {
            rowsPerPage: assignedPaginationModel.pageSize,
            page: assignedPaginationModel.page + 1,
          },
        },
        HEADERS,
      );

      if (response.status === 204) {
        setAssignedVisits([]);
        setAssignedRowCount(0);
        return;
      }

      setAssignedVisits(response.data?.rows ?? []);
      setAssignedRowCount(response.data?.totalRows ?? 0);
    } catch (error) {
      console.log(error);
      getToast({
        status: 'error',
        message: error.response?.data?.message,
        duration: 4,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch assigned visits data when tab is 2
  useEffect(() => {
    if (selectedTab === 2) {
      fetchAssignedVisits();
    }
  }, [selectedTab]);

  const assignTech = async (visitId) => {
    try {
      await customAxios.patch(
        `${GlobalVariables.getURL()}assignTech/${visitId}`,
        {
          technical_id: id,
        },
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        },
      );
      getToast({ status: 'success', title: 'Servicio asignado exitosamente.' });
      fetchData();
    } catch (err) {
      console.log('🚀 ~ err:', err);
    }
  };

  const locale = useTableLocale();

  const unassignedVisitsColumns = [
    {
      field: 'actions',
      headerName: '',
      width: 110,
      filterable: false,
      renderCell: (params) => (
        <Button variant='text' onClick={() => assignTech(params.row.id)}>
          Asignar
        </Button>
      ),
    },
    {
      field: 'id',
      headerName: 'ID',
      width: 120,
      filterable: false,
    },
    {
      field: 'client',
      headerName: 'Cliente',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
      renderCell: (params) => (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
          }}
        >
          <Typography>{params.row.client}</Typography>
          <Typography variant='caption'>{params.row.site}</Typography>
        </div>
      ),
    },
    {
      field: 'visit_type',
      headerName: 'Tipo de servicio',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
    {
      field: 'visit_date',
      headerName: 'Fecha de compromiso',
      width: 150,
      filterable: false,
    },
  ];

  const assignedVisitsColumns = [
    {
      field: 'actions',
      headerName: '',
      width: 110,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant='text'
          onClick={() => navigate(`/visits/details/${params.row.id}`)}
        >
          Ver
        </Button>
      ),
    },
    {
      field: 'id',
      headerName: 'ID',
      width: 120,
      filterable: false,
    },
    {
      field: 'client',
      headerName: 'Cliente',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
      renderCell: (params) => (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
          }}
        >
          <Typography>{params.row.client}</Typography>
          <Typography variant='caption'>{params.row.site}</Typography>
        </div>
      ),
    },
    {
      field: 'visit_type',
      headerName: 'Tipo de servicio',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
    {
      field: 'visit_date',
      headerName: 'Fecha de compromiso',
      width: 150,
      filterable: false,
    },
  ];

  const handlePaginationModelChange = async (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
    setIsLoading(true);

    try {
      const response = await customAxios.post(
        `${GlobalVariables.getURL()}unAssignedVisits`,
        {
          pagination: {
            rowsPerPage: newPaginationModel.pageSize,
            page: newPaginationModel.page + 1,
          },
        },
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        },
      );

      if (response.status === 204) {
        setAvailableVisits([]);
        setRowCount(0);
        return;
      }

      setAvailableVisits(response.data?.rows ?? []);
      setRowCount(response.data?.totalRows ?? 0);
    } catch (error) {
      console.log(error);
      getToast({
        status: 'error',
        title: error.response?.data?.message,
        duration: 4,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssignedPaginationModelChange = async (newPaginationModel) => {
    setAssignedPaginationModel(newPaginationModel);
    const endpoint = `${GlobalVariables.getURL()}getAssignedVisitsForTechDetails/${id}`;
    setIsLoading(true);

    try {
      const response = await customAxios.post(
        endpoint,
        {
          pagination: {
            rowsPerPage: newPaginationModel.pageSize,
            page: newPaginationModel.page + 1,
          },
        },
        HEADERS,
      );

      if (response.status === 204) {
        setAssignedVisits([]);
        setAssignedRowCount(0);
        return;
      }

      setAssignedVisits(response.data?.rows ?? []);
      setAssignedRowCount(response.data?.totalRows ?? 0);
    } catch (error) {
      console.log(error);
      getToast({
        status: 'error',
        message: error.response?.data?.message,
        duration: 4,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToVisitDetails = () => {
    if (techDetails.visit_info.id) {
      navigate(`/visits/details/${techDetails.visit_info.id}`);
    } else {
      navigate(`/visits`);
    }
  };

  //#region Map Region
  const mapRef = useRef(null);

  const onLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const mapOptions = useMemo(
    () => ({
      clickableIcons: false,
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,
    }),
    [],
  );

  const Map = () => {
    const { isLoaded } = useJsApiLoader({
      googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    });

    if (!isLoaded) {
      return <></>;
    }

    return (
      <GoogleMap
        ref={mapRef}
        center={{
          lat: techDetails.tech_location.lat,
          lng: techDetails.tech_location.long,
        }}
        zoom={15}
        onLoad={onLoad}
        mapContainerClassName='techDetailsMap'
        options={mapOptions}
      >
        <Marker
          position={{
            lat: techDetails.tech_location.lat,
            lng: techDetails.tech_location.long,
          }}
          icon={{
            path: 'M256 32c-17.7 0-32 14.3-32 32v2.3 99.6c0 5.6-4.5 10.1-10.1 10.1c-3.6 0-7-1.9-8.8-5.1L157.1 87C83 123.5 32 199.8 32 288v64H544l0-66.4c-.9-87.2-51.7-162.4-125.1-198.6l-48 83.9c-1.8 3.2-5.2 5.1-8.8 5.1c-5.6 0-10.1-4.5-10.1-10.1V66.3 64c0-17.7-14.3-32-32-32H256zM16.6 384C7.4 384 0 391.4 0 400.6c0 4.7 2 9.2 5.8 11.9C27.5 428.4 111.8 480 288 480s260.5-51.6 282.2-67.5c3.8-2.8 5.8-7.2 5.8-11.9c0-9.2-7.4-16.6-16.6-16.6H16.6z',
            fillColor: 'red',
            fillOpacity: 1,
            strokeWeight: 0,
            scale: 0.05,
          }}
        />
      </GoogleMap>
    );
  };
  //#endregion

  const TechDetailsCard = () => {
    return (
      <Box>
        <Grid container>
          <Grid item xs={7}>
            <Box className='techInfo'>
              <Typography variant='h4'>
                {techDetails.name + ' ' + techDetails.lastname}
              </Typography>
              <Typography>
                <b>Teléfono:</b> {techDetails.cellphone}
              </Typography>
              <Typography>
                <b>Correo electrónico:</b> {techDetails.mail}
              </Typography>
            </Box>
          </Grid>
          <Grid
            item
            xs
            display='flex'
            justifyContent='space-between'
            alignItems='center'
          >
            <Box>
              <Typography className='techStatusName'>
                <b>Estatus: </b>
                {techDetails.tech_status}
              </Typography>
              {techDetails.visit_info && techDetails.visit_info.id ? (
                <>
                  <Box display='flex'>
                    <Typography>
                      <b>Plaza: </b>
                      {techDetails.visit_info.client_name}
                    </Typography>
                    <Typography ml={2}>
                      <b>Sucursal: </b>
                      {techDetails.visit_info.site_name}
                    </Typography>
                  </Box>
                  <Typography className='techStatusName'>
                    <b>Tipo de servicio: </b>
                    {techDetails.visit_info.vt_name}
                  </Typography>
                </>
              ) : (
                <Box>
                  <Typography>Técnico sin ningún servicio asignado</Typography>
                </Box>
              )}
            </Box>
            <Box
              display='flex'
              flexDirection='column'
              alignItems='center'
              justifyContent='center'
            >
              <FontAwesomeIcon icon={'screwdriver-wrench'} size='3x' />
              <Button onClick={handleGoToVisitDetails}>Ver más</Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const TechLogsContainer = () => {
    return (
      <Box className='techLogsContainer'>
        <Grid
          item
          xs={7}
          display='flex'
          flexDirection='column'
          height='100%'
          justifyContent='space-evenly'
        >
          <Box flex={6} mx={2} height='100%'>
            <Typography fontWeight='bold'>Bitácora</Typography>
            <Box className='techContainer'>
              <Box className='techList'>
                {Logs !== null ? (
                  Object.entries(Logs).map(([date, logs], index) => (
                    <Accordion
                      key={date}
                      defaultExpanded={index === 0 ? true : false}
                    >
                      <AccordionSummary expandIcon={<ArrowDropDownIcon />}>
                        <Typography fontWeight='bold'>Fecha: {date}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {logs.map((log, index) => (
                          <div key={index}>
                            <Typography>{`${log.time} - ${log.reason}`}</Typography>
                          </div>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  ))
                ) : (
                  <Box
                    display='flex'
                    alignItems='center'
                    justifyContent='center'
                    minHeight='10vh'
                  >
                    <Typography color='#4a4a4a'>
                      No existen registros de este técnico
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          <Box flex={6} mx={2}>
            <Box display='flex' justifyContent='space-between'>
              <Tabs
                value={selectedTab}
                onChange={(_, newValue) => setSelectedTab(newValue)}
              >
                <Tab value={1} label='Tarea sin asignar' />
                <Tab value={2} label='Tareas asignadas' />
              </Tabs>
            </Box>

            {selectedTab === 1 && (
              <Box className='visitsContainer' height='100%'>
                <DataGridPro
                  localeText={
                    locale.components.MuiDataGrid.defaultProps.localeText
                  }
                  sx={{
                    '& .MuiDataGrid-columnHeaders': {
                      display: 'none',
                    },
                  }}
                  loading={isLoading}
                  rows={availableVisits ?? []}
                  rowCount={rowCount ?? 0}
                  columns={unassignedVisitsColumns}
                  pagination
                  paginationMode='server'
                  paginationModel={paginationModel}
                  pageSizeOptions={[5]}
                  onPaginationModelChange={handlePaginationModelChange}
                  initialState={{
                    sorting: {
                      sortModel: [{ field: 'id', sort: 'desc' }],
                    },
                    columns: {
                      columnVisibilityModel: {
                        id: true,
                      },
                    },
                  }}
                  slots={{
                    noRowsOverlay: () => (
                      <Stack
                        height='100%'
                        alignItems='center'
                        justifyContent='center'
                      >
                        No hay visitas sin asignar
                      </Stack>
                    ),
                    toolbar: null, // Toolbar with default functionality
                  }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: false,
                    },
                  }}
                />
              </Box>
            )}

            {selectedTab === 2 && (
              <Box className='visitsContainer' height='100%'>
                <DataGridPro
                  localeText={
                    locale.components.MuiDataGrid.defaultProps.localeText
                  }
                  sx={{
                    '& .MuiDataGrid-columnHeaders': {
                      display: 'none',
                    },
                  }}
                  loading={isLoading}
                  rows={assignedVisits ?? []}
                  rowCount={assignedRowCount ?? 0}
                  columns={assignedVisitsColumns}
                  pagination
                  paginationMode='server'
                  paginationModel={assignedPaginationModel}
                  pageSizeOptions={[5]}
                  onPaginationModelChange={handleAssignedPaginationModelChange}
                  initialState={{
                    sorting: {
                      sortModel: [{ field: 'id', sort: 'desc' }],
                    },
                    columns: {
                      columnVisibilityModel: {
                        id: true,
                      },
                    },
                  }}
                  slots={{
                    noRowsOverlay: () => (
                      <Stack
                        height='100%'
                        alignItems='center'
                        justifyContent='center'
                      >
                        No hay visitas asignadas
                      </Stack>
                    ),
                    toolbar: null, // Toolbar with default functionality
                  }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: false,
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item xs={5} height='100%'>
          {techDetails.tech_location ? (
            <Map />
          ) : (
            <Box
              bgcolor='#ececec'
              height='100%'
              display='flex'
              alignItems='center'
              flexDirection='column'
              justifyContent='center'
            >
              <HistoryOutlinedIcon color='disabled' sx={{ fontSize: '3rem' }} />
              <Typography textAlign='center' width='100%' color='#c4c4c4'>
                No hay datos de su última localización
              </Typography>
            </Box>
          )}
        </Grid>
      </Box>
    );
  };

  return (
    <div className='rootContainerTech'>
      <ToastContainer />
      {isLoading ? (
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          paddingY={10}
          height='100%'
        >
          <CircularProgress />
        </Box>
      ) : techDetails !== null ? (
        <Grid
          container
          className='techDetailsContainer'
          flexDirection='column'
          flexWrap='nowrap'
        >
          <Grid item xs='auto' width='100%'>
            <TechDetailsCard />
            <Divider sx={{ opacity: 1, marginBottom: '1vh' }} />
          </Grid>
          <Grid item xs height='100%'>
            <TechLogsContainer />
          </Grid>
        </Grid>
      ) : (
        <></>
      )}
    </div>
  );
};

export default TechDetails;
